import "./instrument"; // Sentry initialization should be imported first!
import { truncateReduxState } from "./instrument"; // Import truncate function
import React from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import * as Sentry from "@sentry/react"; // Import Sentry

import { configureStore } from '@reduxjs/toolkit';

import { Provider } from 'react-redux';
import { thunk } from 'redux-thunk';
import { rootReducer, initialState } from './jsx/reducers/rootReducer'
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { red } from "@material-ui/core/colors";
import App from './App.jsx';
import reportWebVitals from './reportWebVitals';
import { setupFetchInterceptor } from './jsx/utils/fetchInterceptor'; // Import fetch interceptor
import { setupAxiosInterceptors } from './jsx/utils/axiosInterceptor'; // Import axios interceptor
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Setup interceptors to capture HTTP errors in Sentry
setupFetchInterceptor();
setupAxiosInterceptors();

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
	// Configure Redux enhancer to truncate state instead of removing it
	// This keeps useful context while preventing oversized payloads
	stateTransformer: (state) => {
		try {
			// Truncate Redux state to keep structure but limit size
			// Max depth: 2 levels, Max size: 5000 bytes
			return truncateReduxState(state, 2, 5000);
		} catch (_) {
			return {
				'[error]': 'Unable to transform Redux state',
			};
		}
	},
	// Truncate action data if too large
	actionTransformer: (action) => {
		try {
			const actionStr = JSON.stringify(action);
			if (actionStr.length > 1000) {
				// Truncate payload if too large
				let truncatedPayload = action.payload;
				if (action.payload && typeof action.payload === 'object') {
					try {
						const payloadStr = JSON.stringify(action.payload);
						if (payloadStr.length > 800) {
							truncatedPayload = truncateReduxState(action.payload, 1, 800);
						}
					} catch (_) {
						truncatedPayload = '[Unable to serialize payload]';
					}
				} else if (typeof action.payload === 'string' && action.payload.length > 800) {
					truncatedPayload = action.payload.substring(0, 800) + '...[truncated]';
				}
				
				return {
					type: action.type,
					payload: truncatedPayload,
					'[original_size]': `${actionStr.length} bytes`,
				};
			}
			return action;
		} catch (_) {
			return { 
				type: action.type || 'UNKNOWN',
				'[error]': 'Unable to transform action',
			};
		}
	},
  });  

const store = configureStore({
	reducer: rootReducer,
	preloadedState: initialState,
	middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false, // Disable default thunk to prevent duplication
    }).concat(thunk),
	enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
	devTools: process.env.NODE_ENV !== 'production', // Active les outils de développement en mode non-production
  });

const theme = createTheme({
	palette: {
		primary: {
			main: "#f2c440"
			// main: "#ffd700"
		},
		secondary: {
			main: "#9575cd"
		},
		inherit: {
			main: "gray"
		},
		error: {
			main: red[300]
		}
	},
	typography: {
		fontSize: 15,
		fontWeight: 500,
	},
	props: {
		MuiTextField: {
			variant: 'outlined'
		}
	}
});


// use-query-params adapeter for React Router 6
const RouteAdapter = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();

	const adaptedHistory = React.useMemo( () => ({
		replace(location) {
			navigate(location, { replace: true, state: location.state });
		},
		push(location) {
			navigate(location, { replace: false, state: location.state });
		},
		}), [navigate]
	);
	return children({ history: adaptedHistory, location });
};

//   // You can also set environment, release version, etc.
//   // environment: process.env.NODE_ENV,
//   // release: "my-project-name@" + process.env.npm_package_version, // Example using package.json version
// });
// // --- End Sentry Initialization ---

ReactDOM.render(
			<Provider store={store}>
				<MuiThemeProvider theme={theme}>
					<BrowserRouter>
						<QueryParamProvider ReactRouterRoute={RouteAdapter}>
							<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
								<App />
								<ToastContainer />
							</Sentry.ErrorBoundary>
						</QueryParamProvider>
					</BrowserRouter>
				</MuiThemeProvider>
	</Provider>,
	document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
